import * as React from "react";
import Paper from "@mui/material/Paper";
import {
  Box, /*CircularProgress, Typography,*/ TableContainer, Table, TableHead,
  TableRow, TableCell, TableBody, Checkbox, Chip, TextField, Select, MenuItem, FormControl
} from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { RowDragHandle } from "./RowDragHandle";
import { useFilter } from "../../../../context/FilterContext";

// PAGINACIÓN COMENTADA: componente que muestra el estado de carga/fin de resultados al hacer scroll infinito.
// const CustomFooter = ({ loadingMore, hasMore, hasRows }) => {
//   if (loadingMore) {
//     return (
//       <Box sx={{
//         width: '100%',
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         py: 1,
//         borderTop: '1px solid #e0e0e0',
//       }}>
//         <CircularProgress size={20} />
//         <Typography sx={{ ml: 2 }} variant="body2" color="textSecondary">Cargando más notas...</Typography>
//       </Box>
//     );
//   }
//
//   if (!hasMore && hasRows) {
//     return (
//       <Box sx={{
//         width: '100%',
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         py: 1,
//         borderTop: '1px solid #e0e0e0',
//       }}>
//         <Typography variant="body2" color="textSecondary">Fin de los resultados</Typography>
//       </Box>
//     );
//   }
//   return null;
// };

const INITIAL_WIDTHS = {
  nota: 250,
  medio: 180,
  tipo: 120,
  clasificacion: 130,
  pagina: 80,
  testigo: 100,
  keywords: 150,
  fechaCapsula: 160,
  fechaCaptura: 160,
};

const normalizeSearchText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

function ResizeHandle({ field, onResizeStart, onResize, onResizeEnd }) {
  const [isDragging, setIsDragging] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    let latestX = startX;
    let frameId = null;
    setIsDragging(true);
    onResizeStart(field);

    const onMouseMove = (moveEvent) => {
      latestX = moveEvent.clientX;
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        onResize(field, latestX - startX);
      });
    };

    const onMouseUp = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
      onResize(field, latestX - startX);
      setIsDragging(false);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      onResizeEnd();
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  return (
    <Box
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title="Arrastra para redimensionar"
      sx={{
        position: "absolute",
        right: 0,
        top: 0,
        height: "100%",
        width: "12px",
        cursor: "col-resize",
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
      }}
    >
      <Box
        className={isDragging || isHovered ? "resize-bar resize-bar-active" : "resize-bar"}
        sx={{
          width: "2px",
          height: "50%",
          borderRadius: "2px",
          backgroundColor: isDragging || isHovered ? "#1976d2" : "transparent",
          transition: "background-color 0.15s",
        }}
      />
      <Box
        className={isDragging || isHovered ? "resize-bar resize-bar-active" : "resize-bar"}
        sx={{
          width: "2px",
          height: "50%",
          borderRadius: "2px",
          backgroundColor: isDragging || isHovered ? "#1976d2" : "transparent",
          transition: "background-color 0.15s",
        }}
      />
    </Box>
  );
}

export const reorderMultipleRows = (currentRows, sourceIds, targetRowId) => {
  if (!sourceIds || sourceIds.length === 0 || !targetRowId) return currentRows;

  if (sourceIds.includes(targetRowId)) return currentRows;

  const sourceIndices = sourceIds
    .map((id) => currentRows.findIndex((r) => r.id === id))
    .filter((i) => i !== -1);

  if (sourceIndices.length === 0) return currentRows;

  const targetIndex = currentRows.findIndex((r) => r.id === targetRowId);
  if (targetIndex === -1) return currentRows;

  const sortedSourceIndices = [...sourceIndices].sort((a, b) => a - b);
  const draggedRows = sortedSourceIndices.map((i) => currentRows[i]);

  const remainingRows = currentRows.filter((r) => !sourceIds.includes(r.id));
  const newTargetIndex = remainingRows.findIndex((r) => r.id === targetRowId);
  if (newTargetIndex === -1) return currentRows;

  const avgSourceIndex = sourceIndices.reduce((a, b) => a + b, 0) / sourceIndices.length;
  const isMovingDown = targetIndex > avgSourceIndex;

  const insertIndex = isMovingDown ? newTargetIndex + 1 : newTargetIndex;
  remainingRows.splice(insertIndex, 0, ...draggedRows);

  return remainingRows;
};

const HEADER_CELL_SX = {
  bgcolor: "#f5f7fa",
  color: "#455a64",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderBottom: "1px solid #e0e0e0",
  whiteSpace: "nowrap",
  transition: "box-shadow 0.2s",
};

const HEADER_SHADOW = "0 2px 4px -2px rgba(0, 0, 0, 0.18)";

const HEADER_ROW_SX = {
  "&:hover .resize-bar:not(.resize-bar-active)": { backgroundColor: "#bdbdbd" },
};

const TITLE_CLAMP_SX = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
};

const ROW_BASE_SX = {
  cursor: "grab",
  position: "relative",
  "& > td": { borderBottom: "1px solid #eef0f2" },
  "&.MuiTableRow-hover:hover": { bgcolor: "#f7f9fc" },
  "&.Mui-selected": { bgcolor: "rgba(25, 118, 210, 0.06)" },
  "&.Mui-selected:hover": { bgcolor: "rgba(25, 118, 210, 0.1)" },
};

const COLUMNS = [
  {
    field: "nota",
    headerName: "Notas",
    sortable: true,
    renderCell: (params) => (
      <Box
        component="span"
        sx={TITLE_CLAMP_SX}
        title={params.row.notaPlain}
        dangerouslySetInnerHTML={{ __html: params.value || "" }}
      />
    )
  },
  { field: "medio", headerName: "Medio", sortable: true },
  { field: "tipo", headerName: "Tipo", sortable: true },
  { field: "clasificacion", headerName: "Clasificación", sortable: true },
  { field: "pagina", headerName: "Página", sortable: true },
  {
    field: "testigo",
    headerName: "Testigo",
    sortable: false,
    renderCell: (params) => (
      <a href={params.value} target="_blank" rel="noopener noreferrer">
        Ver nota
      </a>
    ),
  },
  { field: "fechaCapsula", headerName: "Fecha cápsula", sortable: true },
  { field: "fechaCaptura", headerName: "Fecha captura", sortable: true },
];

const formatDateTime = (val) => {
  if (!val || val === "-") return "-";
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  const pad = (n) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const getNotaId = (nota) => nota.CAPCLAVE || nota.capclave;

const SortIcon = ({ field, sortConfig }) => {
  if (sortConfig.field !== field) return <UnfoldMoreIcon sx={{ fontSize: 16, color: "#bdbdbd", ml: 0.5 }} />;
  if (sortConfig.direction === "asc") return <ArrowUpwardIcon sx={{ fontSize: 16, color: "#1976d2", ml: 0.5 }} />;
  return <ArrowDownwardIcon sx={{ fontSize: 16, color: "#1976d2", ml: 0.5 }} />;
};

const selectionBadgeSx = {
  width: 22, height: 22, borderRadius: '4px',
  bgcolor: '#1976d2', color: 'white',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '0.75rem', fontWeight: 'bold', margin: 'auto'
};

const NoteRow = React.memo(function NoteRow({
  row,
  selectionPosition,
  selectedCount,
  isDragOver,
  isDragging,
  onRowClick,
  onCheckboxToggle,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onReorderDragStart,
}) {
  const isSelected = selectionPosition > 0;
  return (
    <TableRow
      hover
      selected={isSelected}
      draggable
      onDragStart={(e) => onDragStart(e, row.id)}
      onDragOver={(e) => onDragOver(e, row.id)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, row.id)}
      onDragEnd={onDragEnd}
      data-id={row.id}
      sx={{
        ...ROW_BASE_SX,
        opacity: isDragging ? 0.5 : 1,
        ...(isDragOver && {
          boxShadow: "inset 0 -2px 0 0 #1976d2, inset 0 2px 0 0 #1976d2"
        })
      }}
    >
      <TableCell sx={{ width: "36px", minWidth: "36px", p: 0.5, textAlign: "center" }}>
        <RowDragHandle
          rowId={row.id}
          isSelected={isSelected}
          selectedCount={selectedCount}
          onReorderDragStart={onReorderDragStart}
        />
      </TableCell>
      <TableCell padding="checkbox" onClick={(e) => onCheckboxToggle(row.id, e)} sx={{ cursor: "default" }}>
        {isSelected ? (
          <Box sx={selectionBadgeSx}>
            {selectionPosition}
          </Box>
        ) : (
          <Checkbox checked={false} sx={{ p: 0.5, pointerEvents: 'none' }} />
        )}
      </TableCell>
      {COLUMNS.map((column) => (
        <TableCell key={column.field} onClick={() => onRowClick(row.id)}>
          {column.renderCell ? column.renderCell({ value: row[column.field], row }) : row[column.field]}
        </TableCell>
      ))}
    </TableRow>
  );
});

export default function TabPanelNotes({ notas, totalNotes = 0, totalMedia = 0, onSelectNota, onSelectMultipleNotas /*, onLoadMore, hasMore, loadingMore */ }) {
  const { avanzarSeleccionSignal } = useFilter();
  const prevAvanzarSignalRef = React.useRef(avanzarSeleccionSignal);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchType, setSearchType] = React.useState("both");
  const [reorderedRows, setReorderedRows] = React.useState(null);
  const [dragOverRowId, setDragOverRowId] = React.useState(null);
  const [draggingRowId, setDraggingRowId] = React.useState(null);
  const dragIdsRef = React.useRef([]);
  const selectedRowsRef = React.useRef(selectedRows);
  selectedRowsRef.current = selectedRows;

  React.useEffect(() => {
    setReorderedRows(null);
    setSelectedRows([]);
  }, [notas]);

  const [colWidths, setColWidths] = React.useState(() => {
    try {
      const saved = localStorage.getItem("tabPanelNotes_colWidths");
      const parsed = saved ? JSON.parse(saved) : {};
      return { ...INITIAL_WIDTHS, ...parsed };
    } catch {
      return INITIAL_WIDTHS;
    }
  });

  const [sortConfig, setSortConfig] = React.useState({ field: null, direction: null });
  const startWidthsRef = React.useRef({});
  const isResizingRef = React.useRef(false);
  const [isScrolled, setIsScrolled] = React.useState(false);

  const handleContainerScroll = React.useCallback((e) => {
    const scrolled = e.currentTarget.scrollTop > 0;
    setIsScrolled((prev) => (prev === scrolled ? prev : scrolled));
  }, []);

  const headerCellSx = React.useMemo(
    () => ({ ...HEADER_CELL_SX, boxShadow: isScrolled ? HEADER_SHADOW : "none" }),
    [isScrolled],
  );

  // PAGINACIÓN COMENTADA: ref para evitar múltiples llamadas de load-more durante el scroll infinito.
  // const loadingRef = React.useRef(loadingMore);
  // React.useEffect(() => {
  //   loadingRef.current = loadingMore;
  // }, [loadingMore]);

  // PAGINACIÓN COMENTADA: al llegar cerca del final del scroll se cargan más notas.
  // const { scrollTop, scrollHeight, clientHeight } = scrollEl;
  // if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && onLoadMore && !loadingRef.current) {
  //   loadingRef.current = true;
  //   onLoadMore();
  // }

  const handleResizeStart = (field) => {
    isResizingRef.current = true;
    startWidthsRef.current = { ...colWidths };
  };

  const handleResize = (field, diff) => {
    const nextWidth = Math.max(50, startWidthsRef.current[field] + diff);
    setColWidths((prev) => (prev[field] === nextWidth ? prev : { ...prev, [field]: nextWidth }));
  };

  const handleResizeEnd = () => {
    setTimeout(() => {
      isResizingRef.current = false;
    }, 0);
    setColWidths((prev) => {
      try {
        localStorage.setItem("tabPanelNotes_colWidths", JSON.stringify(prev));
      } catch {
        return prev;
      }
      return prev;
    });
  };

  const handleSort = (field) => {
    if (isResizingRef.current) return;
    setSortConfig((prev) => {
      if (prev.field === field) {
        if (prev.direction === "asc") return { field, direction: "desc" };
        if (prev.direction === "desc") return { field: null, direction: null };
      }
      return { field, direction: "asc" };
    });
  };

  const rows = React.useMemo(() => (notas || []).map((nota) => ({
    id: getNotaId(nota),
    nota: nota.CAPTITULO,
    notaPlain: String(nota.CAPTITULO || "").replace(/<[^>]*>/g, ""),
    medio: nota.MEDIO,
    tipo: nota.TIPO,
    clasificacion: nota.CLASIFICACION || "-",
    pagina: nota.PAGINA || "-",
    testigo: nota.CAPURLORIGINAL,
    fechaCapsula: formatDateTime(nota.CAPFCAPSULA),
    fechaCaptura: formatDateTime(nota.CAPFCAPTURA),
    fullData: nota,
  })), [notas]);

  const notasById = React.useMemo(() => {
    const map = new Map();
    (notas || []).forEach((nota) => map.set(getNotaId(nota), nota));
    return map;
  }, [notas]);

  const searchIndex = React.useMemo(() => {
    const index = new Map();
    rows.forEach((row) => {
      const { fullData } = row;
      index.set(row.id, {
        title: normalizeSearchText(fullData.CAPTITULO),
        clave: String(fullData.CAPCLAVE || fullData.capclave || ""),
        // La búsqueda por palabra clave solo devuelve la síntesis (CAPTEXTSINT); la de tema también trae CAPTEXTCOMP
        text: normalizeSearchText(fullData.CAPTEXTCOMP || fullData.CAPTEXTSINT),
      });
    });
    return index;
  }, [rows]);

  const filteredRows = React.useMemo(() => {
    const normalizedTerm = normalizeSearchText(searchTerm);
    if (!normalizedTerm) return rows;
    return rows.filter((row) => {
      const entry = searchIndex.get(row.id);
      const titleMatch = entry.title.includes(normalizedTerm) || entry.clave.includes(normalizedTerm);
      const textMatch = entry.text.includes(normalizedTerm);

      if (searchType === "title_clave") return titleMatch;
      if (searchType === "text") return textMatch;
      return titleMatch || textMatch;
    });
  }, [rows, searchIndex, searchTerm, searchType]);

  const sortedRows = React.useMemo(() => {
    if (!sortConfig.field || !sortConfig.direction) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const aVal = a[sortConfig.field] ?? "";
      const bVal = b[sortConfig.field] ?? "";
      const cmp = String(aVal).localeCompare(String(bVal), "es", { numeric: true });
      return sortConfig.direction === "asc" ? cmp : -cmp;
    });
  }, [filteredRows, sortConfig]);

  const sortedRowsRef = React.useRef(sortedRows);
  sortedRowsRef.current = sortedRows;

  const selectionPositions = React.useMemo(() => {
    const positions = new Map();
    selectedRows.forEach((id, index) => positions.set(id, index + 1));
    return positions;
  }, [selectedRows]);

  const handleSelectionChange = React.useCallback((newSelection, isSelectAll = false) => {
    setSelectedRows(newSelection);
    if (onSelectNota) {
      if (newSelection.length > 0) {
        if (!isSelectAll || newSelection.length === 1) {
          const lastSelectedId = newSelection[newSelection.length - 1];
          const selectedNota = notasById.get(lastSelectedId);
          if (selectedNota) onSelectNota(selectedNota);
        }
      } else {
        onSelectNota(null);
      }
    }
    if (onSelectMultipleNotas) {
      const selectedNotas = newSelection.map((id) => notasById.get(id)).filter(Boolean);
      onSelectMultipleNotas(selectedNotas);
    }
  }, [notasById, onSelectNota, onSelectMultipleNotas]);

  const handleSelectionChangeRef = React.useRef(handleSelectionChange);
  handleSelectionChangeRef.current = handleSelectionChange;

  const handleRowClick = React.useCallback((rowId) => {
    handleSelectionChangeRef.current([rowId]);
  }, []);

  const handleCheckboxToggle = React.useCallback((rowId, e) => {
    e.stopPropagation();
    const current = selectedRowsRef.current;
    const newSelected = current.includes(rowId)
      ? current.filter((id) => id !== rowId)
      : [...current, rowId];
    handleSelectionChangeRef.current(newSelected);
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement?.tagName;
      if (activeTag === "INPUT" || activeTag === "TEXTAREA") return;

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        
        if (sortedRows.length === 0) return;
        
        if (selectedRows.length === 0) {
          handleSelectionChange([sortedRows[0].id]);
          setTimeout(() => {
            const rowEl = document.querySelector(`tr[data-id="${sortedRows[0].id}"]`);
            if (rowEl) rowEl.scrollIntoView({ block: "nearest" });
          }, 0);
          return;
        }

        const lastSelectedId = selectedRows[selectedRows.length - 1];
        const currentIndex = sortedRows.findIndex(r => r.id === lastSelectedId);
        
        if (currentIndex === -1) return;

        let nextIndex = currentIndex;
        if (e.key === "ArrowDown") {
          nextIndex = Math.min(currentIndex + 1, sortedRows.length - 1);
        } else if (e.key === "ArrowUp") {
          nextIndex = Math.max(currentIndex - 1, 0);
        }

        if (nextIndex !== currentIndex) {
          const nextRow = sortedRows[nextIndex];
          
          if (e.shiftKey) {
            handleSelectionChange([...new Set([...selectedRows, nextRow.id])]);
          } else {
            handleSelectionChange([nextRow.id]);
          }
          
          setTimeout(() => {
            const rowEl = document.querySelector(`tr[data-id="${nextRow.id}"]`);
            if (rowEl) {
              rowEl.scrollIntoView({ block: "nearest" });
            }
          }, 0);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [sortedRows, selectedRows, handleSelectionChange]);

  React.useEffect(() => {
    if (avanzarSeleccionSignal === prevAvanzarSignalRef.current) return;
    prevAvanzarSignalRef.current = avanzarSeleccionSignal;

    if (sortedRows.length === 0 || selectedRows.length !== 1) return;

    const currentIndex = sortedRows.findIndex((r) => r.id === selectedRows[0]);
    if (currentIndex === -1) return;

    const nextIndex = currentIndex + 1;
    if (nextIndex >= sortedRows.length) return;

    const nextRow = sortedRows[nextIndex];
    handleSelectionChange([nextRow.id]);

    setTimeout(() => {
      const rowEl = document.querySelector(`tr[data-id="${nextRow.id}"]`);
      if (rowEl) rowEl.scrollIntoView({ block: "nearest" });
    }, 0);
  }, [avanzarSeleccionSignal, sortedRows, selectedRows, handleSelectionChange]);

  const handleDragStart = React.useCallback((e, rowId) => {
    const current = selectedRowsRef.current;
    let dragIds = [rowId];
    if (current.includes(rowId) && current.length > 1) {
      dragIds = current;
    }
    e.dataTransfer.setData("application/json", JSON.stringify({ type: "NOTES_DRAG", ids: dragIds }));
    e.dataTransfer.effectAllowed = "copy";
  }, []);

  const handleReorderDragStart = React.useCallback((e, rowId) => {
    const targetRowId = rowId || e.currentTarget?.getAttribute("data-id");
    if (!targetRowId) return;
    const current = selectedRowsRef.current;
    let dragIds = [targetRowId];
    if (current.includes(targetRowId) && current.length > 1) {
      dragIds = current;
    }
    dragIdsRef.current = dragIds;
    setDraggingRowId(targetRowId);
    e.dataTransfer.setData("application/json", JSON.stringify({ type: "NOTES_REORDER", ids: dragIds }));
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleReorderDragOver = React.useCallback((e, rowId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (rowId !== undefined && rowId !== null) {
      setDragOverRowId((prev) => (prev === rowId ? prev : rowId));
    }
  }, []);

  const handleReorderDragLeave = React.useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (e.clientY <= rect.top || e.clientY >= rect.bottom) {
      setDragOverRowId(null);
    }
  }, []);

  const handleReorderDrop = React.useCallback((e, targetRowId) => {
    e.preventDefault();
    setDragOverRowId(null);
    setDraggingRowId(null);
    
    try {
      const dataStr = e.dataTransfer.getData("application/json");
      if (!dataStr) return;
      const data = JSON.parse(dataStr);
      if (data.type !== "NOTES_REORDER" || !data.ids?.length) return;
      
      setReorderedRows((prev) => reorderMultipleRows(prev || sortedRowsRef.current, data.ids, targetRowId));
    } catch (err) {
      console.error("Error al reordenar notas:", err);
    }
  }, []);

  const handleReorderDragEnd = React.useCallback(() => {
    setDraggingRowId(null);
    setDragOverRowId(null);
    dragIdsRef.current = [];
  }, []);

  const displayRows = reorderedRows || sortedRows;
  const selectedCount = selectedRows.length;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Paper sx={{ width: "100%", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ p: 1.5, display: "flex", gap: 2, alignItems: "center", borderBottom: "1px solid #e0e0e0" }}>
          <TextField
            label="Buscar..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <MenuItem value="both">Ambos</MenuItem>
              <MenuItem value="title_clave">Título</MenuItem>
              <MenuItem value="text">Texto</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
          <TableContainer onScroll={handleContainerScroll} sx={{ height: "100%", overflow: "auto" }}>
            <Table stickyHeader size="small" aria-label="tabla de notas">
              <TableHead>
                <TableRow sx={HEADER_ROW_SX}>
                  <TableCell sx={{ ...headerCellSx, width: "36px", minWidth: "36px", p: 0.5 }} />
                  <TableCell padding="checkbox" sx={headerCellSx}>
                    <Checkbox
                      indeterminate={selectedRows.length > 0 && selectedRows.length < filteredRows.length}
                      checked={filteredRows.length > 0 && selectedRows.length === filteredRows.length}
                      onChange={(e) => {
                        const allRowIds = filteredRows.map((r) => r.id);
                        handleSelectionChange(e.target.checked ? allRowIds : [], true);
                      }}
                    />
                  </TableCell>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.field}
                      data-field={column.field}
                      style={{ width: colWidths[column.field], minWidth: colWidths[column.field] }}
                      onClick={() => column.sortable && handleSort(column.field)}
                      title={
                        column.sortable
                          ? sortConfig.field === column.field
                            ? sortConfig.direction === "asc"
                              ? "Ordenar descendente"
                              : "Quitar ordenamiento"
                            : "Ordenar ascendente"
                          : undefined
                      }
                      sx={{ ...headerCellSx, cursor: column.sortable ? "pointer" : "default", userSelect: "none" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {column.headerName}
                        {(column.field === "nota" || column.field === "medio") && (
                          <Chip
                            label={column.field === "nota" ? totalNotes : totalMedia}
                            size="small"
                            sx={{ ml: 0.5, height: 18, fontSize: "0.7rem" }}
                          />
                        )}
                        {column.sortable && <SortIcon field={column.field} sortConfig={sortConfig} />}
                      </Box>
                      {column.field in INITIAL_WIDTHS && (
                        <ResizeHandle
                          field={column.field}
                          onResizeStart={handleResizeStart}
                          onResize={handleResize}
                          onResizeEnd={handleResizeEnd}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((row) => {
                  const selectionPosition = selectionPositions.get(row.id) || 0;
                  return (
                    <NoteRow
                      key={row.id}
                      row={row}
                      selectionPosition={selectionPosition}
                      selectedCount={selectionPosition > 0 ? selectedCount : 0}
                      isDragOver={dragOverRowId === row.id}
                      isDragging={draggingRowId === row.id}
                      onRowClick={handleRowClick}
                      onCheckboxToggle={handleCheckboxToggle}
                      onDragStart={handleDragStart}
                      onDragOver={handleReorderDragOver}
                      onDragLeave={handleReorderDragLeave}
                      onDrop={handleReorderDrop}
                      onDragEnd={handleReorderDragEnd}
                      onReorderDragStart={handleReorderDragStart}
                    />
                  );
                })}
              </TableBody>
            </Table>
            {/* PAGINACIÓN COMENTADA: footer de carga infinita.
            <CustomFooter
              loadingMore={loadingMore}
              hasMore={hasMore}
              hasRows={rows.length > 0}
            />
            */}
          </TableContainer>
        </Box>
      </Paper>
    </Box>
  );
}